import type { TenantRes } from '../res'
import { tenantApplicationType, tenantType } from '../type'
import * as dateLib from './date_lib'

export type Tenant = {
  id?: number
  startDate?: string
  endDate?: string | null
}

export type TenantLike = TenantRes | Tenant

// Helper function to normalize dates to strings for comparison
const normalizeDate = (date: string | Date): string => {
  if (date instanceof Date) {
    return dateLib.formatDate(date)
  }
  return date
}

export const isFuture = (tenant: TenantLike, date: string | null): boolean => {
  if (!tenant.startDate) return false
  const baseDate = date ?? dateLib.getToday()
  const startDate = normalizeDate(tenant.startDate)
  return startDate > baseDate
}

export const isCurrent = (tenant: TenantLike, date: string | null): boolean => {
  if (!tenant.startDate) return false
  const baseDate = date ?? dateLib.getToday()
  const startDate = normalizeDate(tenant.startDate)
  if (startDate > baseDate) return false
  if (!tenant.endDate) return true
  const endDate = normalizeDate(tenant.endDate)
  return endDate >= baseDate
}

export const isPast = (tenant: TenantLike, date: string | null): boolean => {
  if (!tenant.startDate) return true
  const baseDate = date ?? dateLib.getToday()
  const startDate = normalizeDate(tenant.startDate)
  if (startDate > baseDate) return false
  if (!tenant.endDate) return false
  const endDate = normalizeDate(tenant.endDate)
  return endDate < baseDate
}

/**
 * キャンセル可能かどうか（endDateがnullの場合のみ）
 * @param tenant - テナントオブジェクト
 * @returns キャンセル可能な場合はtrue
 */
export const canCancel = (tenant: TenantLike): boolean => {
  return tenant.endDate === null
}

/**
 * 復活可能かどうか（endDateがnullでない場合のみ）
 * @param tenant - テナントオブジェクト
 * @returns 復活可能な場合はtrue
 */
export const canRevival = (tenant: TenantLike): boolean => {
  return tenant.endDate !== null
}

/**
 * 削除可能かどうか（startDateが今日より未来の場合のみ）
 * @param tenant - テナントオブジェクト
 * @returns 削除可能な場合はtrue
 */
export const canDelete = (tenant: TenantLike): boolean => {
  if (!tenant.startDate) return false
  const today = dateLib.getToday()
  const startDate = normalizeDate(tenant.startDate)
  return startDate > today
}

type KeyValueItem = {
  readonly key: string
  readonly value: string
}

/**
 * key-value配列からキーに対応するラベル（value）を取得する汎用関数
 * @param items - { key, value } の配列
 * @param key - 検索するキー
 * @param fallback - 該当なしの場合に返す値（デフォルト: "-"）
 * @returns 対応するラベル。該当なしの場合はfallbackを返す
 */
export function getLabel<T extends readonly KeyValueItem[]>(
  items: T,
  key: string | null | undefined,
  fallback = '-',
): string {
  if (key == null) return fallback
  const item = items.find((i) => i.key === key)
  return item?.value ?? fallback
}

/**
 * テナントの用途から日本語ラベルを取得する
 * @param purpose - 用途コード
 * @returns 用途の日本語ラベル
 */
export function getPurposeLabel(purpose?: string): string {
  const found = tenantType.TENANT_PURPOSES.find((p) => p.key === purpose)
  return found?.value ?? purpose ?? '-'
}

/**
 * 契約区分から日本語ラベルを取得する
 * @param contractType - 契約区分コード（'individual' | 'corporation'）
 * @returns 契約区分の日本語ラベル（'個人' | '法人'）
 */
export function getContractTypeLabel(contractType?: string | null): string {
  if (!contractType) return '-'
  const found = tenantApplicationType.CONTRACT_TYPES.find(
    (c) => c.key === contractType,
  )
  return found?.value ?? contractType
}

/**
 * テナントのステータスから日本語ラベルを取得する
 * @param tenant - テナント情報
 * @returns ステータスの日本語ラベル
 */
export function getStatusLabel(tenant: TenantLike): string {
  if (isFuture(tenant, null)) {
    return '入居予定'
  }
  if (isCurrent(tenant, null)) {
    return '入居中'
  }
  if (isPast(tenant, null)) {
    return '退去済み'
  }
  return '不明'
}

/**
 * テナントのステータスからBadgeのvariantを取得する
 * @param tenant - テナント情報
 * @returns Badgeのvariant
 */
export function getStatusVariant(
  tenant: TenantLike,
): 'default' | 'secondary' | 'destructive' | 'outline' {
  if (isFuture(tenant, null)) {
    return 'secondary'
  }
  if (isCurrent(tenant, null)) {
    return 'default'
  }
  if (isPast(tenant, null)) {
    return 'outline'
  }
  return 'outline'
}

/**
 * 3つの電話番号フィールドを結合してフォーマットする
 * @param phone1 - 市外局番
 * @param phone2 - 市内局番
 * @param phone3 - 加入者番号
 * @returns フォーマットされた電話番号（例: "03-1234-5678"）
 */
export function formatPhone(
  phone1: string,
  phone2: string,
  phone3: string,
): string {
  return `${phone1}-${phone2}-${phone3}`
}
